import React, { useCallback, useMemo } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAllUsersQuery, useGetUserByIdQuery } from '../../../store/user/user'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { ReactComponent as StepQueuedIcon } from '../../../assets/images/cross-client-steps-queued.svg'
import { DEFAULT_AI_BATCH_SIZE, type ScheduledCrossClientQueryAutomation } from '../../../models/ScheduledCrossClientQuery'
import { ScheduledQueryFrequencyPicker } from './ScheduledQueryFrequencyPicker'
import { formatNextRun, getNextRun, type ScheduledQueryFrequency } from './scheduledQueryFrequency'

const CHECKPOINT_OPTIONS: { label: string; value: number | null }[] = [
  { label: '10 clients', value: 10 },
  { label: '20 clients', value: 20 },
  { label: 'Analyze all', value: null },
]

interface ScheduledQueryFormProps {
  name: string
  onNameChange: (name: string) => void
  runAsUserId: string
  onRunAsUserIdChange: (userId: string) => void
  frequency: ScheduledQueryFrequency
  onFrequencyChange: (frequency: ScheduledQueryFrequency) => void
  automation: ScheduledCrossClientQueryAutomation
  onAutomationChange: (automation: ScheduledCrossClientQueryAutomation) => void
  runAsDisabled?: boolean
  submitLabel: string
  submitting: boolean
  onSubmit: () => void
  onCancel: () => void
}

export const ScheduledQueryForm: React.FC<ScheduledQueryFormProps> = ({
  name,
  onNameChange,
  runAsUserId,
  onRunAsUserIdChange,
  frequency,
  onFrequencyChange,
  automation,
  onAutomationChange,
  runAsDisabled = false,
  submitLabel,
  submitting,
  onSubmit,
  onCancel,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const { hasScope } = useZocksAuth()
  const canRunAsOthers = hasScope('**', 'schd_ccq:admin', ['schd_ccq:admin'])

  // The disabled dropdown only displays the selected user, so fetch that one user instead of every page
  const { data: users } = useGetAllUsersQuery({ accountId, identityTypes: ['idp'] }, { skip: !canRunAsOthers || runAsDisabled })
  const { data: runAsUser } = useGetUserByIdQuery({ accountId, userId: runAsUserId }, { skip: !canRunAsOthers || !runAsDisabled })

  const labelOf = useCallback((user: { id: string; name: string }) => (user.id === currentUser.id ? `${user.name} (You)` : user.name), [currentUser.id])

  const userOptions: SelectOption[] = useMemo(() => {
    if (runAsDisabled) {
      return runAsUser ? [{ id: runAsUser.data.id, label: labelOf(runAsUser.data) }] : []
    }
    return (users?.data ?? []).map((user) => ({ id: user.id, label: labelOf(user) }))
  }, [users, runAsUser, runAsDisabled, labelOf])
  const selectedUserOption = userOptions.find((option) => option.id === runAsUserId) ?? null

  const nextRun = useMemo(() => getNextRun(frequency), [frequency])

  return (
    <div className='d-flex flex-column zocks-gap-2'>
      <div className='d-flex flex-column gap-2'>
        <div className='d-flex flex-column'>
          <span className='paragraph-medium medium'>Query name</span>
          <span className='paragraph-small zocks-text-gray-700'>Give it a name or keep the question as is.</span>
        </div>
        <Form.Control type='text' aria-label='Query name' value={name} onChange={(e) => onNameChange(e.target.value)} />
      </div>
      {canRunAsOthers ? (
        <div className='d-flex flex-column gap-2'>
          <div className='d-flex flex-column'>
            <span className='paragraph-medium medium'>Run as</span>
            <span className='paragraph-small zocks-text-gray-700'>
              This query will run for the selected user. They&apos;ll see the results and receive notifications.
            </span>
          </div>
          <div className='scheduled-query-run-as'>
            <SelectDropdown
              options={userOptions}
              initialSelectedOption={selectedUserOption}
              placeholder='Select a user'
              searchable
              hideClearButton
              disabled={runAsDisabled}
              onOptionSelected={(option) => onRunAsUserIdChange(option.id)}
            />
          </div>
        </div>
      ) : null}
      <div className='d-flex flex-column gap-3'>
        <div className='d-flex flex-column'>
          <span className='paragraph-medium medium'>Frequency</span>
          <span className='paragraph-small zocks-text-gray-700'>Choose how often this query runs automatically.</span>
        </div>
        <ScheduledQueryFrequencyPicker frequency={frequency} onChange={onFrequencyChange} />
        {nextRun ? (
          <div className='d-flex align-items-center gap-2 zocks-p-0.75 zocks-bg-color-gray-100 border border-1 zocks-border-color-gray-300 rounded-2'>
            <StepQueuedIcon width={16} height={16} aria-hidden='true' />
            <span className='paragraph-small'>
              Next run: <span className='medium'>{formatNextRun(nextRun)}</span>. Results will be ready at the start of the day.
            </span>
          </div>
        ) : null}
      </div>
      <div className='d-flex flex-column gap-3'>
        <div className='d-flex flex-column'>
          <span className='paragraph-medium medium'>Automate analysis</span>
          <span className='paragraph-small zocks-text-gray-700'>Run additional steps automatically each time this query runs.</span>
        </div>
        <div className='d-flex flex-column gap-2'>
          <Form.Check
            type='checkbox'
            id='automate-find-new-matches'
            label='Find new matches using integrations'
            checked={automation.findNewMatches}
            onChange={(e) => onAutomationChange({ ...automation, findNewMatches: e.target.checked })}
          />
          <Form.Check
            type='checkbox'
            id='automate-confirm-potential-integrations'
            label='Confirm potential matches using integrations'
            checked={automation.confirmPotentialMatchesWithIntegrations}
            onChange={(e) => onAutomationChange({ ...automation, confirmPotentialMatchesWithIntegrations: e.target.checked })}
          />
          <Form.Check
            type='checkbox'
            id='automate-confirm-potential-full-review'
            label='Confirm potential matches with full review (up to checkpoint below)'
            checked={automation.confirmPotentialMatchesWithFullReview}
            onChange={(e) =>
              onAutomationChange({
                ...automation,
                confirmPotentialMatchesWithFullReview: e.target.checked,
                aiBatchSize: e.target.checked ? (automation.aiBatchSize ?? DEFAULT_AI_BATCH_SIZE) : null,
              })
            }
          />
        </div>
      </div>
      <div className='d-flex flex-column gap-3'>
        <div className='d-flex flex-column'>
          <span className='paragraph-medium medium'>Analysis checkpoint</span>
          <span className='paragraph-small zocks-text-gray-700'>
            I&apos;ll pause after this many clients during the full review and notify you to review results.
          </span>
        </div>
        <div className='d-flex gap-2'>
          {CHECKPOINT_OPTIONS.map((option) => (
            <Form.Check
              key={option.label}
              type='radio'
              name='analysis-checkpoint'
              id={`analysis-checkpoint-${option.value ?? 'all'}`}
              label={option.label}
              disabled={!automation.confirmPotentialMatchesWithFullReview}
              checked={automation.confirmPotentialMatchesWithFullReview && (automation.aiBatchSize ?? null) === option.value}
              onChange={() => onAutomationChange({ ...automation, aiBatchSize: option.value })}
              className='flex-fill border border-1 zocks-border-color-gray-300 rounded-2 zocks-p-0.75'
            />
          ))}
        </div>
      </div>
      <div className='d-flex justify-content-end gap-2'>
        <Button variant='secondary' className='button-medium' onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant='primary' className='button-medium' onClick={onSubmit} loading={submitting} disabled={!name.trim()}>
          {submitLabel}
        </Button>
      </div>
    </div>
  )
}
